<template>
  <div>
    <!----------------------Sublets ----------------------->
    <div class="mb-4"
      v-if="selectedTab.type == ItemCategoryTypes.SUBL || selectedViewType != viewTypes.tabbedView">
      <quote-builder-sublet-headings-component :name="sublCategoryObject?.displayName || 'Sublets'"
        :count="subletLength"
        :total-amount="subletsTotal"
        :is-full-width="isFullWidth" />
      <div class="rows">
        <draggable @change="itemMoved($event, ItemCategoryTypes.SUBL)"
          v-model="innerValue.sublets"
          @over.prevent
          @enter.prevent
          v-bind="{'disabled': !isRowDraggable, chosen: 'chosen', ghostClass: 'draggable-ghost', dragClass: 'draggable-drag'}"
          :clone="$event => cloneHandler($event, ItemCategoryTypes.SUBL)"
          :group="{ name: ItemCategoryTypes.SUBL, pull: 'clone', put: true }"
          handle=".item-drag-handle">
          <quote-builder-sublet-item v-for="item in subletItems"
            :key="item.id"
            :inner-value="innerValue"
            :is-full-width="isFullWidth"
            :items-assessments="itemsAssessments"
            :item="item"
            :edit-item-id="editItemObj.id"
            :vehicle="vehicle"
            :sublets="sublets"
            :vendors="vendors"
            :is-editable="!readOnlyComputed"
            @deleteItem="item => deleteItem(item)"
            @focusNextRow="$emit('focusNextRow', item)"
            @handlePresentAs="itemId => $emit('handlePresentAs', itemId)"
            @itemRowClicked="(value, event, close) => $emit('handleItemRowClick',value, event, close)" />
        </draggable>
      </div>
      <div>
        <button class="button is-outlined is-primary"
          @click="addBlankItem()"
          :disabled="readOnlyComputed">
          + Add Item
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QuoteBuilderSubletHeadingsComponent from './QuoteBuilderSubletHeadingsComponent.vue'
import QuoteBuilderSubletItem from './QuoteBuilderSubletItem.vue'
import draggable from 'vuedraggable'
import { ItemCategoryTypes, EventHubTypes } from '@/enums'
import cloneDeep from 'lodash.clonedeep'
import { QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuoteItemAutoSaveDeleteMixin } from '../../mixins'
import QuoteService from '../../.././QuoteService'
import VendorService from '@/views/vendor/VendorService'
import Guid from '../../../../../components/Guid'
import { QuoteItemModel } from '@/classes/viewmodels'

export default {
  name: 'QuoteBuilderSubletComponent',
  components: {
    QuoteBuilderSubletHeadingsComponent,
    QuoteBuilderSubletItem,
    draggable
  },
  mixins: [QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuoteItemAutoSaveDeleteMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    isFullWidth: {
      type: Boolean,
      required: true
    },
    editItemObj: {
      type: Object
    },
    selectedTab: {
      type: Object
    },
    selectedViewType: {
      type: String
    },
    isAudanet: {
      type: Boolean,
      default: false
    },
    viewTypes: {
      type: Object
    },
    vehicle: {
      type: Object
    },
    nextLineNumber: {
      type: Number,
      default: 0
    },
    isEditable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      innerValue: null,
      sublets: [],
      vendors: [],
      isSyncingParentUpdate: false,
      lastEmittedSubletsSignature: null
    }
  },
  computed: {
    ItemCategoryTypes() {
      return ItemCategoryTypes
    },
    subletItems() {
      return this.innerValue.sublets
    },
    subletLength() {
      return this.innerValue.sublets.filter((i) => !(i.deleted || i.isDeleted)).length
    },
    subletsTotal() {
      return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.innerValue.sublets, ItemCategoryTypes.SUBL)
    },
    isRowDraggable() {
      return !this.innerValue.readOnly
    },
    itemsAssessments() {
      return this.getItemsAssessments(this.innerValue.sublets, this.innerValue.quoteAssessments)
    },
    sublCategoryObject() {
      return this.$company.itemCategories.find((i) => i.itemCategoryType === ItemCategoryTypes.SUBL)
    },
    readOnlyComputed() {
      return this.innerValue.readOnly || !this.isEditable
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) return
        const currentSignature = this.computeSubletsSignature(newVal.sublets)
        if (this.isSyncingParentUpdate && currentSignature === this.lastEmittedSubletsSignature) {
          this.isSyncingParentUpdate = false
          return
        }
        // Fast-path: skip deepDiff(entire_entity) when only sibling-derived totals changed
        const innerSubletsSignature = this.computeSubletsSignature(this.innerValue?.sublets)
        if (currentSignature === innerSubletsSignature &&
            this.innerValue?.quotingMethod === newVal.quotingMethod &&
            this.innerValue?.readOnly === newVal.readOnly) {
          return
        }
        this.innerValue = cloneDeep(newVal)
      },
      deep: true
    },
    innerValue: {
      handler(newVal) {
        if (!newVal || this.isSyncingParentUpdate) return
        const nextSignature = this.computeSubletsSignature(newVal.sublets)
        if (nextSignature === this.lastEmittedSubletsSignature) return
        this.lastEmittedSubletsSignature = nextSignature
        this.isSyncingParentUpdate = true
        // Merge with latest parent value to avoid emitting stale sibling arrays (Parts, Misc, etc.) when only labours changed.
        const toEmit = { ...this.value, sublets: cloneDeep(newVal.sublets) }
        this.$emit('handleUpdate', toEmit)
      },
      deep: true
    }
  },
  mounted() {
    this.$v.entity.sublets.$touch()
    this.$eventHub.$on('updateSublets', this.updateSublets)
    this.$eventHub.$on('addBlankItemSublet', this.addBlankItem)
    this.$eventHub.$on('deleteSubletItem', this.deleteItem)
  },
  beforeDestroy() {
    this.$eventHub.$off('updateSublets', this.updateSublets)
    this.$eventHub.$off('addBlankItemSublet', this.addBlankItem)
    this.$eventHub.$off('deleteSubletItem', this.deleteItem)
  },
  created() {
    this.innerValue = cloneDeep(this.value)
    this.getSublets()
    this.getVendors()
    this.lastEmittedSubletsSignature = this.computeSubletsSignature(this.value?.sublets)
    // Value is committed to vm.entity after the field's model-update delay.
    // The item watcher in QuoteView triggers autosave once the value propagates.
    this.$watch('innerValue', function () {
    }, { deep: true })
  },
  methods: {
    computeSubletsSignature(items = []) {
      try {
        return JSON.stringify(items)
      } catch (e) {
        return null
      }
    },
    updateSublets() {
      const newItems = this.innerValue.sublets.filter((i) => i.isNew)

      this.innerValue = cloneDeep(this.value)

      // if there are new items, add them to the innerValue
      if (newItems.length) {
        newItems.forEach((item) => {
          // check if the item already exists in the innerValue
          const existingItem = this.innerValue.sublets.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)
          if (!existingItem && item.quoteItemId == this.editItemObj.id) {
            // if it doesn't exist, add it
            this.innerValue.sublets.push(item)
          }
        })
      }
      // compare two objects to see if they are the same, sometimes the parent
      // value might be updated by innervalue and we don't want to update the inner value again
      // const diff = deepDiff(this.innerValue, this.value)
      // if (diff) {
      //   const hasSubletsDiff = diff.some((d) => d.path && d.path[0] === 'sublets')

      //   const newInnerValue = cloneDeep(this.value)
      //   // if parent/child arrays are different, merge them
      //   if (hasSubletsDiff) {
      //     // items that exist in child but not in parent (could be added when tabbing, enter or arrow down)
      //     const newSubletItems = this.innerValue.sublets.filter((i) => i.isNew)
      //     // if these items don't exist in the new value, add them
      //     newSubletItems.forEach((item) => {
      //       if (!newInnerValue.sublets.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)) {
      //         newInnerValue.sublets.push(item)
      //       }
      //     })
      //   }

      //   this.innerValue = newInnerValue
      // }
    },
    cloneHandler(item, type) {
      // change the quoteItemId to a new guid so we can find it later and change to correct type on drop
      // TODO: looks like this doesn't work, leave it as is
      // item.quoteItemId = Guid.newGuid()
      return item
    },
    itemMoved(event, type) {
      // if an item was dragged from one list to another, i.e from RR to RWA, Parts to RR etc.
      if (event?.added) {
        const item = event.added.element
        this.handleItemAdded(item, type, event.added.newIndex)
      }
      // console.log(event.moved)
      let sortNo = this.innerValue.sublets.length ? Math.min(...this.innerValue.sublets.map((i) => i.sortNo || 1)) : 1
      this.innerValue.sublets.forEach((item) => {
        item.sortNo = sortNo
        sortNo++
      })
      this.$eventHub.$emit(EventHubTypes.AutoSaveQuoteItems)
      // Emit directly — don't rely on the deep watcher to detect in-place sortNo mutations
      const nextSignature = this.computeSubletsSignature(this.innerValue.sublets)
      this.lastEmittedSubletsSignature = nextSignature
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, sublets: cloneDeep(this.innerValue.sublets) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })
    },
    handleItemAdded(item, type, index) {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', item.itemDesc, ItemCategoryTypes.SUBL)
      newItem.lineNumber = this.innerValue.audatex === null ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.value = 0
      newItem.sortNo = this.innerValue.sublets.length ? Math.max(...this.innerValue.sublets.map((i) => i.sortNo)) + 1 : 1
      this.innerValue.lines = this.nextLineNumber + 1
      // replace the item with the new item
      this.innerValue.sublets.splice(index, 1, newItem)

      this.$emit('onItemCopied', newItem)
    },
    async getSublets() {
      this.sublets = await QuoteService.getSubletsProto(!this.innerValue.audatex)
    },
    async getVendors() {
      const filter = {
        id: Guid.empty(),
        name: '',
        pageIndex: 1,
        pageSize: 50
      }

      const vendors = await VendorService.getVendorDropdownProto2(filter)

      this.vendors = vendors
    },
    addBlankItem() {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', '', ItemCategoryTypes.SUBL)
      newItem.lineNumber = this.innerValue.audatex === null ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.value = 0
      newItem.sortNo = this.innerValue.sublets.length ? Math.max(...this.innerValue.sublets.map((i) => i.sortNo)) + 1 : 1
      this.innerValue.sublets.splice(this.innerValue.sublets.length, 1, newItem)
      this.innerValue.lines = this.nextLineNumber + 1
      this.$notification.success('', 'Item added')

      // this.$toast.open({
      //   message: 'Item added',
      //   type: 'is-success',
      //   position: 'is-bottom',
      //   queue: false
      // })

      this.$nextTick(() => {
        this.$emit('newItemAdded', newItem.quoteItemId)
      })
    },
    deleteItem(item) {
      if (item.isNew) {
        const itemIndex = this.innerValue.sublets
          .map(function (obj) {
            return obj.quoteItemId
          })
          .indexOf(item.quoteItemId)
        if (itemIndex >= 0) {
          this.innerValue.sublets.splice(itemIndex, 1)
          // this.items.splice(index, 1)
        }
      } else {
        this.autoSaveDeleteExistingItem(item)
      }

      // Sync the deletion into the Add-Item panel so it isn't re-added.
      this.$eventHub.$emit(EventHubTypes.QuoteItemDeletedSync, item)

      // Propagate the deletion to the parent directly (mirrors itemMoved) rather than
      // relying on the innerValue watcher, which drops emits while isSyncingParentUpdate
      // is true — otherwise a later value echo resets innerValue and resurrects the item.
      this.lastEmittedSubletsSignature = this.computeSubletsSignature(this.innerValue.sublets)
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, sublets: cloneDeep(this.innerValue.sublets) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })

      this.$emit('onItemDeleted', item.lineNumber)

      // this.updateTotal()
      // this.$toast.open({
      //   message: `Item <span class="is-italic">${item.itemDesc}</span> deleted`,
      //   type: 'is-danger',
      //   position: 'is-top',
      //   actionText: 'Undo',
      //   onAction: () => {
      //     if (item.isNew) {
      //       this.innerValue.sublets.splice(this.innerValue.sublets.length, 1, item)
      //       // this.items.splice(this.items.length, 1, item)
      //     } else {
      //       item.deleted = false
      //       item.isDeleted = false
      //       item.itemStatus = originalItemStatus
      //     }
      //     // this.updateTotal()
      //   }
      // })
    }
  }
}
</script>

<style scoped>
/* Your component's styles go here */
</style>